/**
 * Draggable Widget Card - Displays a widget preview in the store drawer
 * Can be dragged to the dashboard to add the widget
 */

import React from 'react'
import { useDraggable } from '@dnd-kit/core'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from 'react-i18next'
import { GripVertical, Plus } from 'lucide-react'
import { WidgetDefinition } from '../widget-registry'

interface DraggableStoreWidgetProps {
    widget: WidgetDefinition
    onAdd?: (widgetId: string) => void
}

const DraggableStoreWidget: React.FC<DraggableStoreWidgetProps> = ({
    widget,
    onAdd,
}) => {
    const { t } = useTranslation()
    const { attributes, listeners, setNodeRef, transform, isDragging } =
        useDraggable({
            id: `store-${widget.id}`,
            data: {
                type: 'store-widget',
                widget,
            },
        })

    const style = {
        transform: CSS.Translate.toString(transform),
        opacity: isDragging ? 0.5 : 1,
    }

    const Icon = widget.icon

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`
                group relative flex items-center gap-3 p-3 bg-white border border-gray-200
                rounded-lg cursor-grab active:cursor-grabbing hover:border-primary/50
                hover:shadow-sm transition-all
                ${isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}
            `}
            {...attributes}
            {...listeners}
        >
            {/* Drag handle */}
            <div className="text-gray-300 group-hover:text-gray-400">
                <GripVertical className="w-4 h-4" />
            </div>

            {/* Icon */}
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <Icon className="w-4 h-4 text-primary" />
            </div>

            {/* Widget info */}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                    {t(widget.nameKey)}
                </p>
                <p className="text-xs text-gray-500 truncate">
                    {t(widget.descriptionKey)}
                </p>
            </div>

            {/* Quick add button */}
            {onAdd && (
                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        onAdd(widget.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md bg-primary/10
                        hover:bg-primary/20 text-primary transition-opacity"
                    title={t('dashboard.add-widget')}
                >
                    <Plus className="w-4 h-4" />
                </button>
            )}

            {/* Col span badge */}
            <div className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                {widget.defaultColSpan}/12
            </div>
        </div>
    )
}

export default DraggableStoreWidget
